/**
 * @license
 * Copyright (c) 2008 %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Point, lineCardinal, lineCardinalClosed, lineMonotone } from './lineUtils';

const SPLINE_TYPE_MONOTONE_VERTICAL = 'mv';
const SPLINE_TYPE_MONOTONE_HORIZONTAL = 'mh';
const SPLINE_TYPE_CARDINAL_CLOSED = 'cc';

/**
 * Returns a curved path command, based on cubic hermite splines, that goes through the points in the points array.
 * @param {array} points Polyline points array.
 * @param {boolean} connectWithLine Whether the first point is reached using lineTo. Otherwise, moveTo is used.
 * @param {string} type The spline type.
 * @return {string} Path commands.
 */
export function curveThroughPoints(points: Point[], connectWithLine: boolean, splineType: string) {
  if (points.length == 0) return '';

  const prefix = connectWithLine ? 'L' : 'M';

  if (splineType == SPLINE_TYPE_MONOTONE_VERTICAL) {
    return prefix + lineMonotone(points, false);
  } else if (splineType == SPLINE_TYPE_MONOTONE_HORIZONTAL) {
    return prefix + lineMonotone(points, true);
  } else if (splineType == SPLINE_TYPE_CARDINAL_CLOSED) {
    return prefix + lineCardinalClosed(points, 0.7);
  } else {
    return prefix + lineCardinal(points, 0.7);
  }
}

/**
 * Returns a path command for a move to the specified coordinates
 * @param x the destination x coordinate
 * @param y the destination y coordinate
 * @returns the moveTo path command
 */
export function moveTo(x: number, y: number) {
  return 'M' + x + ',' + y;
}

/**
 * Returns a path command for a line to the specified coordinates
 * @param x the destination x coordinate
 * @param y the destination y coordinate
 * @returns the lineTo path command
 */
export function lineTo(x: number, y: number) {
  return 'L' + x + ',' + y;
}

/**
 * Returns a path command for a vertical line to the specified x coordinate
 * @param x the destination x coordinate
 * @returns the horizontal line path command
 */
export function horizontalLineTo(x: number) {
  return 'H' + x;
}

/**
 * Returns a path command for a horizontal line to the specified y coordinate
 * @param y the destination y coordinate
 * @returns the vertical line path command
 */
export function verticalLineTo(y: number) {
  return 'V' + y;
}

/**
 * Returns a path command for a rectangle.
 * @param x Rectangle x.
 * @param y Rectangle y.
 * @param w Rectangle width.
 * @param h Rectangle height.
 * @returns Path command.
 */
function rectangle(x: number, y: number, w: number, h: number) {
  const cmd =
    moveTo(x, y) +
    horizontalLineTo(x + w) +
    verticalLineTo(y + h) +
    horizontalLineTo(x) +
    closePath();

  return cmd;
}

/**
 * Returns a path command for an arc to the specified coordinates
 * @param rx the x radius of the ellipse whose arc will be drawn
 * @param ry the y radius of the ellipse whose arc will be drawn
 * @param angleExtent the sweep of the arc to be drawn
 * @param direction 1 for clockwise, 0 for counter-clockwise
 * @param x the ending x coordinate
 * @param y the ending y coordinate
 */
export function arcTo(
  rx: number,
  ry: number,
  angleExtent: number,
  direction: number,
  x: number,
  y: number
) {
  let cmd = 'A' + rx + ',' + ry + ',0,';
  if (angleExtent > Math.PI) {
    cmd += '1,';
  } else {
    cmd += '0,';
  }
  cmd += direction + ',' + x + ',' + y;
  return cmd;
}

/**
 * Returns a path command that closes the path.
 */
export function closePath() {
  return 'Z';
}

/**
 * Parse corner radii and return the new shape.
 *
 * Sample (valid) radius values:
 *  '5px' - 5px all corners
 *  '50% 50% 0 0' - 50% top corners, 0 bottom corners
 *  '50% 10' - 50% top left bottom right, 10px top right bottom left.
 *  '5px / 10px' - 5px horizontal radius, 10px vertical radius all corners
 *  '50% 50% 25% 25% / 25% 25% 50% 50%' - 50% h radius 25% v radius top corners, 25% h radius 50% v radius bottom corners
 *
 * Note that all non-% values (including unitless) get interpreted as 'px'.
 *
 * @param x Rectangle x.
 * @param y Rectangle y.
 * @param w Rectangle width.
 * @param h Rectangle height.
 * @param radius The string to be parsed.
 * @param multiplier The value used for when a percent radius is provided
 * @param defaultValue A specified value for the border radius
 * @returns Path command of shape with border radius.
 */
export function rectangleWithBorderRadius(
  x: number,
  y: number,
  w: number,
  h: number,
  radius: string,
  multiplier: number,
  defaultValue: string
) {
  if (radius == '0' || radius === '0px') return rectangle(x, y, w, h);

  let topLeftX = defaultValue;
  let topLeftY = defaultValue;
  let topRightX = defaultValue;
  let topRightY = defaultValue;
  let bottomRightX = defaultValue;
  let bottomRightY = defaultValue;
  let bottomLeftX = defaultValue;
  let bottomLeftY = defaultValue;
  if (radius.indexOf('/') != -1) {
    const splitHorizVert = radius.split('/');
    const horiz = splitHorizVert[0].trim().split(/\s+/);
    const vert = splitHorizVert[1].trim().split(/\s+/);
    if (horiz.length == 1) topLeftX = topRightX = bottomRightX = bottomLeftX = horiz[0];
    else if (horiz.length == 2) {
      topLeftX = bottomRightX = horiz[0];
      topRightX = bottomLeftX = horiz[1];
    } else if (horiz.length == 3) {
      topLeftX = horiz[0];
      topRightX = bottomLeftX = horiz[1];
      bottomRightX = horiz[2];
    } else if (horiz.length == 4) {
      topLeftX = horiz[0];
      topRightX = horiz[1];
      bottomRightX = horiz[2];
      bottomLeftX = horiz[3];
    }
    if (vert.length == 1) topLeftY = topRightY = bottomRightY = bottomLeftY = vert[0];
    else if (vert.length == 2) {
      topLeftY = bottomRightY = vert[0];
      topRightY = bottomLeftY = vert[1];
    } else if (vert.length == 3) {
      topLeftY = vert[0];
      topRightY = bottomLeftY = vert[1];
      bottomRightY = vert[2];
    } else if (vert.length == 4) {
      topLeftY = vert[0];
      topRightY = vert[1];
      bottomRightY = vert[2];
      bottomLeftY = vert[3];
    }
  } else if (radius != 'auto') {
    const split = radius.trim().split(/\s+/);
    if (split.length == 1) {
      topLeftX =
        topRightX =
        bottomRightX =
        bottomLeftX =
        topLeftY =
        topRightY =
        bottomRightY =
        bottomLeftY =
          split[0];
    } else if (split.length == 2) {
      topLeftX = bottomRightX = topLeftY = bottomRightY = split[0];
      topRightX = bottomLeftX = topRightY = bottomLeftY = split[1];
    } else if (split.length == 3) {
      topLeftX = topLeftY = split[0];
      topRightX = bottomLeftX = topRightY = bottomLeftY = split[1];
      bottomRightX = bottomRightY = split[2];
    } else if (split.length == 4) {
      topLeftX = topLeftY = split[0];
      topRightX = topRightY = split[1];
      bottomRightX = bottomRightY = split[2];
      bottomLeftX = bottomLeftY = split[3];
    }
  }

  return roundedRectangle(
    x,
    y,
    w,
    h,
    parseBorderRadiusItem(topLeftX, multiplier),
    parseBorderRadiusItem(topLeftY, multiplier),
    parseBorderRadiusItem(topRightX, multiplier),
    parseBorderRadiusItem(topRightY, multiplier),
    parseBorderRadiusItem(bottomRightX, multiplier),
    parseBorderRadiusItem(bottomRightY, multiplier),
    parseBorderRadiusItem(bottomLeftX, multiplier),
    parseBorderRadiusItem(bottomLeftY, multiplier)
  );
}

/**
 * Parses a single corner radius dimension
 * @param item The x or y radius input need to be parsed
 * @param multiplier The value used for when a percent radius is provided
 * @returns The integer value of the corner radius
 */
function parseBorderRadiusItem(item: string, multiplier: number) {
  let radius = Math.min(parseFloat(item), multiplier / 2);
  if (item.indexOf('%') != -1) {
    radius = Math.min(50, parseFloat(item)) * 0.01 * multiplier;
  }
  return radius;
}

/**
 * Returns a path command for a rounded rectangle.
 * @param x Rectangle x.
 * @param y Rectangle y.
 * @param w Rectangle width.
 * @param h Rectangle height.
 * @param tlcrX Top left corner x radius.
 * @param tlcrY Top left corner y radius.
 * @param trcrX Top right corner x radius.
 * @param trcrY Top right corner y radius.
 * @param brcrX Bottom right corner x radius.
 * @param brcrY Bottom right corner y radius.
 * @param blcrX Bottom left corner x radius.
 * @param blcrY Bottom left corner  y radius.
 * @returns Path command.
 */
function roundedRectangle(
  x: number,
  y: number,
  w: number,
  h: number,
  tlcrX: number,
  tlcrY: number,
  trcrX: number,
  trcrY: number,
  brcrX: number,
  brcrY: number,
  blcrX: number,
  blcrY: number
) {
  tlcrY = Math.min(tlcrY, 0.5 * h);
  trcrY = Math.min(trcrY, 0.5 * h);
  brcrY = Math.min(brcrY, 0.5 * h);
  blcrY = Math.min(blcrY, 0.5 * h);
  tlcrX = Math.min(tlcrX, 0.5 * w);
  trcrX = Math.min(trcrX, 0.5 * w);
  brcrX = Math.min(brcrX, 0.5 * w);
  blcrX = Math.min(blcrX, 0.5 * w);
  const cmd =
    moveTo(x + tlcrX, y) +
    lineTo(x + w - trcrX, y) +
    arcTo(trcrX, trcrY, Math.PI / 2, 1, x + w, y + trcrY) +
    lineTo(x + w, y + h - brcrY) +
    arcTo(brcrX, brcrY, Math.PI / 2, 1, x + w - brcrX, y + h) +
    lineTo(x + blcrX, y + h) +
    arcTo(blcrX, blcrY, Math.PI / 2, 1, x, y + h - blcrY) +
    lineTo(x, y + tlcrY) +
    arcTo(tlcrX, tlcrY, Math.PI / 2, 1, x + tlcrX, y) +
    closePath();

  return cmd;
}
